import React from "react";

import { faKey } from "@fortawesome/pro-regular-svg-icons";
import { useController } from "react-hook-form";

import { Card } from "src/base-components/Card";
import { Pill } from "src/base-components/Pill";
import { OrganizationUser, SimpleRoleType } from "src/clients/taktile-api";
import { Skeleton } from "src/design-system/Skeleton";
import { useCurrentUserId } from "src/store/AuthStore";
import { Status } from "src/userManagement/Status";
import { UserStatus } from "src/userManagement/types";
import {
  DUMMY_PERMISSION_SLOTS,
  NO_WORKSPACE_ROLE,
  WORKSPACE_ADMIN_ROLE,
  WORKSPACE_EDITOR_ROLE,
  WORKSPACE_MEMBER_ROLE,
  WORKSPACE_REVIEWER_ROLE,
} from "src/userManagement/userPermissions/permissionFeatures";
import {
  RolePillDropdown,
  RolePillOption,
} from "src/userManagement/userPermissions/RolePillDropdown";
import { UserPermissionsFormValues } from "src/userManagement/userPermissions/utils";

const ORG_ROLE_OPTIONS: RolePillOption[] = [
  { key: SimpleRoleType.ADMIN, label: "Admin" },
  { key: SimpleRoleType.MEMBER, label: "Member" },
];

const WORKSPACE_ROLE_OPTIONS: RolePillOption[] = [
  { key: WORKSPACE_ADMIN_ROLE, label: "Admin" },
  { key: WORKSPACE_MEMBER_ROLE, label: "Member" },
  { key: NO_WORKSPACE_ROLE, label: "None" },
];

const UNSETTABLE_WORKSPACE_ROLE_LABELS: Record<string, string> = {
  [WORKSPACE_EDITOR_ROLE]: "Editor",
  [WORKSPACE_REVIEWER_ROLE]: "Reviewer",
};

const workspaceRoleOptions = (selected: string): RolePillOption[] =>
  WORKSPACE_ROLE_OPTIONS.some((option) => option.key === selected)
    ? WORKSPACE_ROLE_OPTIONS
    : [
        ...WORKSPACE_ROLE_OPTIONS,
        {
          key: selected,
          label: UNSETTABLE_WORKSPACE_ROLE_LABELS[selected] ?? selected,
        },
      ];

const SUMMARY_LABELS = [
  "Org Role",
  "Workspace Role",
  "Email",
  "Status",
  "Permissions slots/keys",
];

const getUserStatus = (user: OrganizationUser): UserStatus => {
  if (!user.authenticated) {
    return "invited";
  }
  if (!user.is_active) {
    return "deactivated";
  }
  return "active";
};

const SummaryColumn: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex min-w-[200px] flex-1 flex-col items-start gap-1 py-4">
    <span className="font-inter-medium-12px text-gray-800">{label}</span>
    <div className="flex h-5 min-w-0 max-w-full items-center">{children}</div>
  </div>
);

const SummaryDivider: React.FC = () => (
  <div className="w-px shrink-0 self-stretch bg-gray-200" />
);

const SummaryShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Card
    className="decideScrollbar overflow-x-auto px-0 py-0"
    dataLoc="user-summary-header"
    variant="inverted"
    fullWidth
  >
    <div className="flex items-stretch gap-5 pr-10 pl-5">{children}</div>
  </Card>
);

export const UserSummaryHeaderSkeleton: React.FC = () => (
  <SummaryShell>
    {SUMMARY_LABELS.map((label, index) => (
      <React.Fragment key={label}>
        {index > 0 && <SummaryDivider />}
        <SummaryColumn label={label}>
          <Skeleton height="h-5" width="w-20" />
        </SummaryColumn>
      </React.Fragment>
    ))}
  </SummaryShell>
);

// The API refuses to change the org role of the caller.
const OrgRolePill: React.FC<{ userId: string }> = ({ userId }) => {
  const currentUserId = useCurrentUserId();
  const { field } = useController<UserPermissionsFormValues, "orgRole">({
    name: "orgRole",
  });

  return (
    <RolePillDropdown
      dataLoc="user-summary-org-role"
      disabled={currentUserId === userId}
      icon={faKey}
      options={ORG_ROLE_OPTIONS}
      selectedKey={field.value}
      onSelect={(key) => field.onChange(key as SimpleRoleType)}
    />
  );
};

const WorkspaceRolePill: React.FC = () => {
  const { field } = useController<UserPermissionsFormValues, "workspaceRole">({
    name: "workspaceRole",
  });

  return (
    <RolePillDropdown
      dataLoc="user-summary-workspace-role"
      icon={faKey}
      options={workspaceRoleOptions(field.value)}
      selectedKey={field.value}
      onSelect={field.onChange}
    />
  );
};

type Props = {
  user: OrganizationUser;
};

export const UserSummaryHeader: React.FC<Props> = ({ user }) => {
  return (
    <SummaryShell>
      <SummaryColumn label="Org Role">
        <OrgRolePill userId={user.id} />
      </SummaryColumn>
      <SummaryDivider />
      <SummaryColumn label="Workspace Role">
        <WorkspaceRolePill />
      </SummaryColumn>
      <SummaryDivider />
      <SummaryColumn label="Email">
        <span
          className="block max-w-full truncate font-inter-normal-13px text-gray-500"
          data-loc="user-summary-email"
        >
          {user.email}
        </span>
      </SummaryColumn>
      <SummaryDivider />
      <SummaryColumn label="Status">
        <Status dataLoc="user-summary-status" status={getUserStatus(user)} />
      </SummaryColumn>
      <SummaryDivider />
      <SummaryColumn label="Permissions slots/keys">
        <Pill
          dataLoc="user-summary-keys"
          fontType="text"
          size="sm"
          variant="indigo-light"
        >
          <Pill.Icon icon={faKey} />
          <Pill.Text>
            {DUMMY_PERMISSION_SLOTS.used}/{DUMMY_PERMISSION_SLOTS.total}
          </Pill.Text>
        </Pill>
      </SummaryColumn>
    </SummaryShell>
  );
};
